// Piezas compartidas de la app de WhatsApp: control de modo, avatar, horas.

import { useId, type KeyboardEvent } from 'react';
import { Bot, PowerOff, Sparkles } from 'lucide-react';

import { MODE_HINT, MODE_LABEL, type ReplyMode } from '../api';

export const MODE_ICON: Record<ReplyMode, typeof Bot> = { auto: Bot, suggest: Sparkles, off: PowerOff };
const MODES: ReplyMode[] = ['auto', 'suggest', 'off'];

/** Selector segmentado de 3 modos (radiogroup accesible con flechas). */
export function ModeControl({
  value, onChange, disabled, busy, compact, label = 'Modo de respuesta del agente', disabledReason,
}: {
  value: ReplyMode;
  onChange: (m: ReplyMode) => void;
  disabled?: boolean;
  busy?: boolean;
  compact?: boolean;
  label?: string;
  disabledReason?: string;
}) {
  const id = useId();
  const onKey = (e: KeyboardEvent) => {
    if (disabled) return;
    const i = MODES.indexOf(value);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onChange(MODES[(i + 1) % 3]); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onChange(MODES[(i + 2) % 3]); }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      {!compact && <span id={id} className="hidden lg:inline text-[12px] text-muted-foreground whitespace-nowrap">Respuestas</span>}
      <div
        role="radiogroup"
        aria-labelledby={compact ? undefined : id}
        aria-label={compact ? label : undefined}
        aria-busy={busy || undefined}
        title={disabled ? disabledReason : undefined}
        onKeyDown={onKey}
        className={`inline-flex rounded-xl border border-border bg-muted p-0.5 ${disabled ? 'opacity-70' : ''}`}
      >
        {MODES.map((m) => {
          const Icon = MODE_ICON[m];
          const sel = value === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={sel}
              tabIndex={sel ? 0 : -1}
              disabled={disabled}
              onClick={() => !sel && onChange(m)}
              title={MODE_HINT[m]}
              className={[
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] px-2 sm:px-2.5 py-1.5 text-[13px] font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
                sel ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                disabled ? 'cursor-not-allowed' : '',
              ].join(' ')}
            >
              <Icon className={`h-3.5 w-3.5 ${sel && m === 'auto' ? 'wa-accent-text' : ''} ${sel && m === 'suggest' ? 'text-amber-600 dark:text-amber-300' : ''}`} aria-hidden />
              <span className={compact ? 'sr-only sm:not-sr-only' : ''}>{MODE_LABEL[m]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const AVATAR_TONES = [
  'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200',
  'bg-sky-500/15 text-sky-800 dark:text-sky-200',
  'bg-violet-500/15 text-violet-800 dark:text-violet-200',
  'bg-amber-500/20 text-amber-900 dark:text-amber-200',
  'bg-rose-500/15 text-rose-800 dark:text-rose-200',
  'bg-teal-500/15 text-teal-800 dark:text-teal-200',
];

export function initials(name: string): string {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  if (/^\d+$/.test(parts[0])) return parts[0].slice(-2);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

export function Avatar({ name, size = 44 }: { name: string; size?: number }) {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className={`shrink-0 rounded-full grid place-items-center font-semibold ${size > 40 ? 'text-[15px]' : 'text-[13px]'} ${AVATAR_TONES[h % AVATAR_TONES.length]}`}
    >
      {initials(name)}
    </span>
  );
}

const TZ = 'America/Mexico_City';

function parse(iso?: string | null): Date | null {
  if (!iso) return null;
  const s = String(iso);
  const d = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayKey(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

/** Hora corta para la lista: hoy → 14:05 · ayer → Ayer · semana → lun · antes → 12/10/26 */
export function listStamp(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '';
  const now = new Date();
  const today = dayKey(now);
  const y = new Date(now.getTime() - 86_400_000);
  if (dayKey(d) === today) return d.toLocaleTimeString('es-MX', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (dayKey(d) === dayKey(y)) return 'Ayer';
  if (now.getTime() - d.getTime() < 6 * 86_400_000) return d.toLocaleDateString('es-MX', { timeZone: TZ, weekday: 'short' });
  return d.toLocaleDateString('es-MX', { timeZone: TZ, day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function timeOnly(iso?: string | null): string {
  const d = parse(iso);
  return d ? d.toLocaleTimeString('es-MX', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }) : '';
}

export function dayLabel(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '';
  const now = new Date();
  if (dayKey(d) === dayKey(now)) return 'Hoy';
  if (dayKey(d) === dayKey(new Date(now.getTime() - 86_400_000))) return 'Ayer';
  return d.toLocaleDateString('es-MX', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
}

export function sameDay(a?: string | null, b?: string | null): boolean {
  const x = parse(a);
  const y = parse(b);
  return !!x && !!y && dayKey(x) === dayKey(y);
}

export function relativeUntil(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '';
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (mins <= 0) return 'vencida';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return `${h} h ${mins % 60} min`;
}

/** Teléfono legible: 5214891289445 → +52 1 489 128 9445 · 4441112233 → 444 111 2233 */
export function prettyPhone(raw?: string | null): string {
  const d = String(raw || '').replace(/\D/g, '');
  if (!d) return String(raw || '');
  if (d.length === 10) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  if (d.length === 12 && d.startsWith('52')) return `+52 ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
  if (d.length === 13 && d.startsWith('521')) return `+52 1 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  if (d.length === 11 && d.startsWith('1')) return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  return `+${d}`;
}

export const STAGE_LABEL: Record<string, string> = {
  lead: 'Lead', prospecto: 'Prospecto', cliente: 'Cliente', unsubscribed: 'Baja', customer: 'Cliente',
};
